<template>
  <div class="p-4 h-screen flex flex-column">
    <ConfirmDialog />
    <div class="grid h-full flex-grow-1 overflow-hidden">
      <div class="col-12 flex flex-column h-full">
        <div class="chat-frame">
          <div class="chat-header">
            <div class="flex flex-column gap-1">
              <span class="text-2xl font-semibold">Jarvin</span>
              <small class="p-text-secondary text-xs hidden md:block">Historique effacé automatiquement après 24h d'inactivité.</small>
            </div>
            <Button label="Nouvelle Conversation" icon="pi pi-plus" @click="newConversation" outlined />
          </div>

          <div ref="chatHistoryRef" class="chat-history">
            <template v-for="(message, index) in history" :key="index">
              <div class="message-group" 
                   :class="message.role === 'user' ? 'user-group' : 'bot-group'"
                   v-if="message.role === 'user' || (message.parts && message.parts[0] && message.parts[0].text && message.parts[0].text.trim().length > 0)">
                <Avatar v-if="message.role === 'bot'" image="/fav_inaubi.png" shape="circle" class="message-avatar" :style="{ 'background-color': botBackgroundColor }" />
                <Avatar v-if="message.role === 'user'" :image="user?.picture" :label="user?.name ? user.name.charAt(0).toUpperCase() : 'U'" shape="circle" class="message-avatar" />
                <div class="message-content-wrapper">
                  <div class="message-bubble" :class="message.role === 'user' ? 'user-bubble' : 'bot-bubble'">
                    <div v-if="message.role === 'user'" v-text="message.parts[0].text"></div>
                    <div v-else v-html="renderMarkdown(message.parts[0].text)"></div>
                  </div>
                  <div class="message-time">{{ formatTime(message.timestamp) }}</div>
                </div>
              </div>
            </template>

            <!-- Thinking Process Block (Visible during streaming) -->
            <div v-if="isStreaming" class="thinking-container mb-2 message-group bot-group" style="max-width: 100%;">
               <Avatar image="/fav_inaubi.png" shape="circle" class="message-avatar" :style="{ 'background-color': botBackgroundColor }" />
               <div style="flex: 1; max-width: 85%;">
                  <Panel toggleable :collapsed="!showThoughts" @toggle="showThoughts = $event.value" class="w-full">
                    <template #header>
                      <div class="flex align-items-center gap-2">
                        <i class="pi pi-spin pi-cog" v-if="toolStatus?.status === 'running'"></i>
                        <i class="pi pi-brain" v-else></i>
                        <span class="font-medium text-sm">
                            {{ toolStatus ? `Exécution: ${toolStatus.tool}` : 'Réflexion en cours...' }}
                        </span>
                      </div>
                    </template>
                    <p class="thinking-content p-0 m-0 text-sm" v-if="currentThought" v-html="renderMarkdown(currentThought)"></p>
                    <p class="thinking-content p-0 m-0 text-sm font-italic text-gray-500" v-else>Analyse de la demande...</p>
                    <div v-if="toolStatus?.result" class="mt-2 p-2 surface-ground border-round text-xs">
                        <strong>Résultat:</strong> {{ toolStatus.result.substring(0, 100) }}...
                    </div>
                  </Panel>
               </div>
            </div>
          </div>

          <div class="chat-input-section">
            <div class="suggestions-container" v-if="suggestions.length > 0">
              <div class="suggestions-wrapper" ref="suggestionsWrapper">
                  <Chip v-for="(suggestion, index) in suggestions"
                        :key="index"
                        :label="suggestion.text"
                        :icon="suggestion.url ? 'pi pi-external-link' : 'pi pi-sparkle'"
                        @click="handleSuggestionClick(suggestion)"
                        class="cursor-pointer shrink-0" />
              </div>
              <Button v-if="isOverflowing" icon="pi pi-chevron-right" class="p-button-rounded p-button-secondary p-button-text scroll-button-right" @click="scrollRight" />
            </div>
            <div class="chat-input-container">
              <Textarea v-model="newMessage" @keyup.enter="sendMessage" placeholder="Posez votre question..." rows="1" autoResize :disabled="isLoading || isStreaming" class="w-full chat-input" />
              <Button icon="pi pi-send" @click="sendMessage" :disabled="isLoading || isStreaming" class="send-button" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, nextTick, onMounted, onUnmounted, computed } from 'vue';
import { useChatStore } from '@/stores/chat';
import { useAuthStore } from '@/stores/auth';
import { useThemeStore } from '@/stores/theme';
import { useMarkdown } from '@/composables/useMarkdown';
import { storeToRefs } from 'pinia';
import { useConfirm } from "primevue/useconfirm";
import Avatar from 'primevue/avatar';
import Textarea from 'primevue/textarea';
import Button from 'primevue/button';
import ConfirmDialog from 'primevue/confirmdialog';
import Chip from 'primevue/chip';
import Panel from 'primevue/panel'; // Import Panel

const chatStore = useChatStore();
const { history, isLoading, suggestions, isStreaming, currentThought, toolStatus } = storeToRefs(chatStore); // Destructure new states

const authStore = useAuthStore();
const { user } = storeToRefs(authStore);

const themeStore = useThemeStore();
const { isDarkMode } = storeToRefs(themeStore);

const { renderMarkdown } = useMarkdown();
const confirm = useConfirm();

const botBackgroundColor = computed(() => isDarkMode.value ? 'var(--p-surface-700)' : 'var(--p-surface-200)');

const newMessage = ref('');
const chatHistoryRef = ref(null);
const suggestionsWrapper = ref(null);
const isOverflowing = ref(false);
const showThoughts = ref(true); // Control for the collapsible panel

const checkOverflow = () => {
  nextTick(() => {
    const el = suggestionsWrapper.value;
    if (el) {
      const atEnd = el.scrollWidth - el.scrollLeft <= el.clientWidth + 1; // +1 for rounding issues
      isOverflowing.value = !atEnd;
    } else {
      isOverflowing.value = false;
    }
  });
};

const scrollRight = () => {
  const el = suggestionsWrapper.value;
  if (el) {
    el.scrollBy({ left: 200, behavior: 'smooth' });
  }
};

const formatTime = (date) => {
  if (!date) return '';
  return new Date(date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const sendMessage = () => {
  if (!newMessage.value.trim() || isLoading.value || isStreaming.value) return;
  chatStore.sendMessage(newMessage.value);
  newMessage.value = '';
  showThoughts.value = true; // Open thoughts panel for new message
};

const handleSuggestionClick = (suggestion) => {
  if (isLoading.value || isStreaming.value) return;
  if (suggestion.url) {
    window.open(suggestion.url, '_blank');
  } else {
    chatStore.sendMessage(suggestion.text);
  }
};

const newConversation = () => {
  confirm.require({
    message: 'Voulez-vous vraiment démarrer une nouvelle conversation ? L\'historique de la conversation actuelle sera supprimé.',
    header: 'Confirmation',
    icon: 'pi pi-info-circle',
    rejectClass: 'p-button-text',
    acceptClass: 'p-button-danger',
    accept: () => {
      chatStore.newConversation();
    },
    rejectLabel: 'Annuler',
    acceptLabel: 'Confirmer',
  });
};

const scrollToBottom = () => {
  nextTick(() => {
    if (chatHistoryRef.value) {
      chatHistoryRef.value.scrollTop = chatHistoryRef.value.scrollHeight;
    }
  });
};

watch(history, () => {
  scrollToBottom();
}, { deep: true });

watch(suggestions, () => {
  checkOverflow();
});

onMounted(() => {
  scrollToBottom();
  // Fetch initial suggestions based on the welcome message
  if (history.value.length <= 1) {
    chatStore.fetchSuggestions();
  }
  checkOverflow();
  window.addEventListener('resize', checkOverflow);
  suggestionsWrapper.value?.addEventListener('scroll', checkOverflow);
});

onUnmounted(() => {
  window.removeEventListener('resize', checkOverflow);
  suggestionsWrapper.value?.removeEventListener('scroll', checkOverflow);
});
</script>

<style scoped>
.chat-frame {
  width: 100%;
  height: 100%;
  background-color: var(--p-surface-card);
  border-radius: var(--p-border-radius);
  box-shadow: var(--p-shadow-2);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.chat-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 1.5rem;
  border-bottom: 2px solid var(--p-surface-200);
}

.chat-history {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden; /* Prevent all horizontal scroll */
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.chat-input-section {
  border-top: 1px solid var(--p-surface-border);
  background-color: var(--p-surface-section);
}

.suggestions-container {
  display: flex;
  align-items: center;
  padding: 0.75rem 1rem 0 1rem;
}

.suggestions-wrapper {
  display: flex;
  gap: 0.5rem;
  overflow-x: auto;
  white-space: nowrap;
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none;  /* IE and Edge */
  flex-grow: 1;
}

.suggestions-wrapper::-webkit-scrollbar {
  display: none; /* Chrome, Safari, and Opera */
}

.scroll-button-right {
  margin-left: 0.5rem;
  background-color: var(--p-surface-section);
}

.message-group {
  display: flex;
  align-items: flex-end;
  gap: 0.75rem;
  max-width: 85%;
}

.bot-group {
  align-self: flex-start;
}

.user-group {
  align-self: flex-end;
  flex-direction: row-reverse;
}

.message-avatar {
  width: 36px;
  height: 36px;
  flex-shrink: 0;
  padding: 5px; /* Add padding to shrink the inner image */
  box-sizing: border-box; /* Ensure padding is included in the element's total size */
}

.message-content-wrapper {
  display: flex;
  flex-direction: column;
}

.bot-group .message-content-wrapper {
  align-items: flex-start;
}

.user-group .message-content-wrapper {
  align-items: flex-end;
}

.message-bubble {
  padding: 0.75rem 1rem;
  border-radius: 18px;
  font-size: 0.875rem;
  /* line-height: 1.5; */
  max-width: 100%;
  word-wrap: break-word;
  white-space: pre-wrap;
  overflow-wrap: break-word;
}

.bot-bubble {
  background-color: v-bind(botBackgroundColor);
  color: var(--p-text-color);
  border-bottom-left-radius: 4px;
}

.user-bubble {
  background-color: var(--p-primary-500);
  color: var(--p-primary-contrast-color);
  border-bottom-right-radius: 4px;
}

.message-time {
  font-size: 0.7rem;
  color: var(--p-text-color-secondary);
  margin-top: 0.25rem;
  padding: 0 0.5rem;
}

.chat-input-container {
  display: flex;
  padding: 0.75rem 1rem;
  align-items: center;
}

.chat-input {
  flex: 1;
  margin-right: 0.75rem;
  resize: none;
}

.send-button {
  flex-shrink: 0;
}

:deep(.message-bubble p) {
  margin: 0;
}

:deep(.message-bubble pre) {
  white-space: pre-wrap;
  word-wrap: break-word;
  overflow-wrap: break-word;
  word-break: break-all;
}

:deep(.message-bubble ul), :deep(.message-bubble ol) {
  margin-top: 0.2rem;
  margin-bottom: 0.2rem;
  padding-left: 1.25rem;
}

:deep(.message-bubble li) {
  margin-bottom: 0.25rem;
}

:deep(.message-bubble li:last-child) {
  margin-bottom: 0;
}

.thinking-container {
  width: 100%;
}

.thinking-content {
  font-style: italic;
  color: var(--p-text-color-secondary);
  white-space: pre-wrap; /* Preserve whitespace and newlines */
  word-break: break-word;
}

/* Override PrimeVue Panel header background for better integration */
:deep(.p-panel-header) {
  background-color: var(--p-surface-hover) !important;
  color: var(--p-text-color) !important;
}

:deep(.p-panel-content) {
  background-color: var(--p-surface-section) !important;
  color: var(--p-text-color) !important;
}
</style>